import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import getUpcomingEvents, {
    GetUpcomingEventsParams,
} from 'api/events/get-upcoming-events';
import dayjs from 'utils/dayjs';
import {displayDateFormat} from 'utils/date-time';

const defaultFromDate = dayjs().format(displayDateFormat);
const defaultToDate = dayjs().add(90, 'day').format(displayDateFormat);

export interface EventFiltersPayload {
    eventTypeIds?: number[];
    destinationIds?: number[];
    eventStatusIds?: string[];
    toDate?: string;
    fromDate?: string;
}
/**
 * Action to reset filters to their initial state
 *
 * Action Type: eventSchedule/resetFilters
 */
export const resetFilters = createAction('eventSchedule/resetFilters');

/**
 * Action to update filters with count of applied filters
 *
 * Action Type: eventSchedule/updateFilters
 */
export const updateFilters = createAction(
    'eventSchedule/updateFilters',
    (payload: EventFiltersPayload) => {
        let appliedFiltersCount = 0;

        Object.entries(payload).forEach(([key, filter]) => {
            if (key === 'fromDate' && filter === defaultFromDate) {
                return;
            }
            if (key === 'toDate' && filter === defaultToDate) {
                return;
            }
            if (Array.isArray(filter) ? filter.length > 0 : Boolean(filter)) {
                appliedFiltersCount++;
            }
        });

        return {
            payload: {
                ...payload,
                numFiltersApplied: appliedFiltersCount,
            },
        };
    }
);

/**
 * Thunk to get the upcoming events data
 *
 * Action Type: eventSchedule/getUpcomingEventsData
 *
 */
export const getUpcomingEventsData = createAsyncThunk(
    'eventSchedule/getUpcomingEventsData',
    async (
        {facilityId, requestData, useISOString}: GetUpcomingEventsParams,
        {rejectWithValue, signal}
    ) => {
        try {
            const response = await getUpcomingEvents({
                facilityId,
                requestData,
                useISOString,
                config: {signal},
            });

            const resultData = response.data.results.map((item) => {
                const {tiers} = item;

                item.totalInventory = tiers.reduce(
                    (partialSum, tier) => partialSum + (tier?.inventory || 0),
                    0
                );

                return item;
            });

            return {
                data: resultData,
                meta: response.meta,
            };
        } catch (err) {
            rejectWithValue(err);
        }
    }
);
